import { useState, useCallback, useEffect } from 'react';
import { createGlobalState } from './createGlobalState';

class AlertManager {
    private _messages: string[] = [];

    get messages() {
        return this._messages;
    }

    private readonly listeners = new Set<() => void>();

    addAlertMessage(message: string) {
        this._messages.push(message);
        this.notify();
    }

    clearAlertMessages() {
        this._messages.splice(0);
        this.notify();
    }

    addListener(listener: () => void) {
        this.listeners.add(listener);
    }

    removeListener(listener: () => void) {
        this.listeners.delete(listener);
    }

    private notify() {
        for (const listener of this.listeners) {
            listener();
        }
    }
}

const {
    useWithState: useWithAlertManager,
    useState: useAlertManager,
} = createGlobalState((_, a) => a, new AlertManager());

export { useWithAlertManager };

export function useAlertTicker() {
    const manager = useAlertManager();

    const [alerting, setAlerting] = useState<'red' | 'green'>('green');

    const addAlertMessage = useCallback((message: string) => {
        manager.addAlertMessage(message);
        setAlerting('red');
    }, [manager]);

    const clearAlertMessages = useCallback(() => {
        manager.clearAlertMessages();
        setAlerting('green');
    }, [manager]);

    const [messages, setMessages] = useState(() => manager.messages);
    useEffect(() => {
        const listener = () => {
            setMessages(manager.messages);
        };
        manager.addListener(listener);
        return () => manager.removeListener(listener);
    }, [manager, setMessages]);

    return {
        alerting,
        messages,
        addAlertMessage,
        clearAlertMessages,
    };
}
